import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, KeyRound, MailCheck } from "lucide-react";
import api, { setToken, formatApiError } from "./api";
import { EASE } from "@/components/Motion";
import { inputCls } from "./components";
import { Logo } from "@/components/Logo";

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotDone, setForgotDone] = useState(false);
  const [forgotError, setForgotError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = isLogin
        ? { identifier: form.email, password: form.password }
        : { name: form.name, email: form.email, password: form.password };
      const { data } = await api.post(isLogin ? "/auth/login" : "/auth/register", payload);
      setToken(data.token);
      navigate("/panel");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotError("");
    setForgotBusy(true);
    try {
      await api.post("/auth/forgot", { email: forgotEmail.trim() });
      setForgotDone(true);
    } catch (err) {
      setForgotError(formatApiError(err));
    } finally {
      setForgotBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-ink px-4 py-16">
      <div className="pointer-events-none absolute -top-32 left-1/3 h-[480px] w-[480px] rounded-full bg-gold/[0.07] blur-[140px]" />
      <div className="noise-overlay pointer-events-none fixed inset-0 opacity-[0.05]" />

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: EASE }}
        className="relative w-full max-w-md"
        data-testid="auth-card"
      >
        <Link
          to="/"
          data-testid="auth-back-link"
          className="mb-8 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] text-neutral-500 transition-colors hover:text-goldlight"
        >
          <ArrowLeft size={14} /> BACK TO SITE
        </Link>

        <div className="rounded-3xl border border-gold/15 bg-panel/80 p-8 backdrop-blur-xl sm:p-10">
          <div className="flex items-center gap-3">
            <Logo className="h-11 w-11 rounded-xl object-cover ring-1 ring-gold/40" />
            <div>
              <div className="text-sm font-bold tracking-[0.2em] text-neutral-100">
                BEATKING<span className="text-gold">SMM</span>
              </div>
              <div className="font-mono text-[9px] tracking-[0.3em] text-gold/70">CUSTOMER PANEL 2.0</div>
            </div>
          </div>

          {forgot ? (
            forgotDone ? (
              <div className="mt-8 text-center" data-testid="forgot-success">
                <MailCheck size={40} className="mx-auto text-emerald-400" />
                <h1 className="mt-5 font-serif text-3xl italic text-neutral-50">Check your inbox.</h1>
                <p className="mt-3 text-sm leading-relaxed text-neutral-400">
                  If <span className="text-goldlight">{forgotEmail}</span> is registered, a reset link is on its way.
                  It expires in <span className="font-semibold text-gold">10 minutes</span>.
                </p>
                <button
                  type="button"
                  onClick={() => { setForgot(false); setForgotDone(false); }}
                  data-testid="forgot-back-to-login"
                  className="mt-6 inline-block rounded-full bg-gradient-to-br from-goldlight via-gold to-golddark px-8 py-3.5 text-sm font-bold text-black transition-shadow duration-300 hover:shadow-[0_8px_30px_rgb(var(--gold)_/_0.35)]"
                >
                  BACK TO LOGIN
                </button>
              </div>
            ) : (
              <>
                <div className="mt-8 flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-gold/40 bg-gold/10 text-gold">
                    <KeyRound size={18} />
                  </span>
                  <h1 className="font-serif text-3xl italic text-neutral-50" data-testid="forgot-heading">Recover access.</h1>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-neutral-400">
                  Enter your account email — we will send a reset link valid for 10 minutes.
                </p>
                <form onSubmit={submitForgot} className="mt-8 space-y-4" data-testid="forgot-form">
                  <input
                    type="email"
                    required
                    placeholder="Email address"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className={inputCls}
                    data-testid="forgot-email-input"
                  />
                  {forgotError && (
                    <motion.p
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="rounded-xl border border-rose-400/30 bg-rose-400/10 px-4 py-3 text-sm text-rose-300"
                      data-testid="forgot-error"
                    >
                      {forgotError}
                    </motion.p>
                  )}
                  <button
                    type="submit"
                    disabled={forgotBusy}
                    data-testid="forgot-submit-button"
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark py-3.5 text-sm font-bold tracking-wide text-black shadow-[0_8px_30px_rgb(var(--gold)_/_0.25)] transition-shadow duration-300 hover:shadow-[0_8px_40px_rgb(var(--gold)_/_0.45)] disabled:opacity-60"
                  >
                    {forgotBusy && <Loader2 size={16} className="animate-spin" />}
                    SEND RESET LINK
                  </button>
                </form>
                <p className="mt-6 text-center text-sm text-neutral-500">
                  Remembered it?{" "}
                  <button
                    type="button"
                    onClick={() => setForgot(false)}
                    data-testid="forgot-cancel-link"
                    className="font-semibold text-gold transition-colors hover:text-goldlight"
                  >
                    Back to sign in
                  </button>
                </p>
              </>
            )
          ) : (
            <>
              <h1 className="mt-8 font-serif text-3xl italic text-neutral-50 sm:text-4xl" data-testid="auth-heading">
                {isLogin ? "Welcome back." : "Claim your seat."}
              </h1>
              <p className="mt-2 text-sm text-neutral-400">
                {isLogin
                  ? "The engine room awaits — 42 Cr+ orders of muscle behind you."
                  : "One account for SMM, brand deals and smarter sourcing."}
              </p>

              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="auth-form">
                {!isLogin && (
                  <input
                    type="text"
                    required
                    placeholder="Full name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className={inputCls}
                    data-testid="auth-name-input"
                  />
                )}
                <input
                  type="email"
                  required
                  placeholder="Email address"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={inputCls}
                  data-testid="auth-email-input"
                />
                <div>
                  <input
                    type="password"
                    required
                    minLength={isLogin ? 1 : 8}
                    placeholder={isLogin ? "Password" : "Password (min. 8 characters)"}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className={inputCls}
                    data-testid="auth-password-input"
                  />
                  {isLogin && (
                    <div className="mt-2 text-right">
                      <button
                        type="button"
                        onClick={() => { setForgot(true); setForgotEmail(form.email); setForgotDone(false); setForgotError(""); }}
                        data-testid="forgot-link"
                        className="text-xs font-medium text-neutral-500 transition-colors hover:text-gold"
                      >
                        Forgot password?
                      </button>
                    </div>
                  )}
                </div>

                {error && (
                  <motion.p
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-xl border border-rose-400/30 bg-rose-400/10 px-4 py-3 text-sm text-rose-300"
                    data-testid="auth-error"
                  >
                    {error}
                  </motion.p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="auth-submit-button"
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark py-3.5 text-sm font-bold tracking-wide text-black shadow-[0_8px_30px_rgb(var(--gold)_/_0.25)] transition-shadow duration-300 hover:shadow-[0_8px_40px_rgb(var(--gold)_/_0.45)] disabled:opacity-60"
                >
                  {loading && <Loader2 size={16} className="animate-spin" />}
                  {isLogin ? "ENTER THE PANEL" : "CREATE ACCOUNT"}
                </button>
              </form>

              {!isLogin && (
                <p className="mt-4 text-center text-[11px] leading-relaxed text-neutral-600" data-testid="signup-terms-note">
                  By creating an account you accept our{" "}
                  <Link to="/terms" target="_blank" className="text-gold underline underline-offset-2 transition-colors hover:text-goldlight">
                    Terms &amp; Conditions
                  </Link>{" "}
                  and Refund Policy.
                </p>
              )}
              <p className="mt-6 text-center text-sm text-neutral-500">
                {isLogin ? "New to the panel? " : "Already have an account? "}
                <Link
                  to={isLogin ? "/panel/signup" : "/panel/login"}
                  data-testid="auth-toggle-link"
                  className="font-semibold text-gold transition-colors hover:text-goldlight"
                >
                  {isLogin ? "Create one" : "Sign in"}
                </Link>
              </p>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
